import { CheckCircleOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { PencilSimple } from "@phosphor-icons/react";
import { Position } from "@common";
import { getCurrentSeason } from "@common/season";
import { InputMask } from "@components/atoms/InputMask/InputMask";
import { FILE_ACCEPT_STRING, validateFileFormat } from "@helpers";
import { useAuth, useJobApplications, useJobPositions } from "@hooks";
import { Button, Col, Divider, Form, Input, message, Modal, Popconfirm, Row, Select, Space, Spin, Typography } from "antd";
import Upload from "antd/es/upload";
import { UploadFile } from "antd/es/upload/interface";
import { useEffect, useRef, useState } from "react";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

// Interface for the job position form
interface JobPositionFormValues {
  title: string;
  location: string;
  type: string;
  department: string;
  salaryRange: string;
  description: string;
  seasonalPeriod?: string;  // Ex: "2026-27 winter season"
  responsibilities?: string[]; // List of responsibilities
  requirements: string[];
  benefits?: string[];
  additionalInfo?: string; // Additional text about the position
  contractPeriod?: string;
}

export const JoinPage = () => {
  const { createJobApplication } = useJobApplications();
  const { jobPositions, addJobPosition, removeJobPosition, updateJobPosition } = useJobPositions();
  const { isAuthenticated } = useAuth();

  const formRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();
  const [jobPositionForm] = Form.useForm();

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isThankYouVisible, setIsThankYouVisible] = useState(false);
  const [appliedPositionTitle, setAppliedPositionTitle] = useState('');
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [isJobFormVisible, setIsJobFormVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPositionId, setEditingPositionId] = useState<string | null>(null);

  // Effect that makes sure the correct id is selected
  useEffect(() => {
    if (jobPositions.data && jobPositions.data.length === 1) {
      setSelectedPositionId(String(jobPositions.data[0].id).trim());
    } else if (jobPositions.data && jobPositions.data.length > 0 && !selectedPositionId) {
      setSelectedPositionId(String(jobPositions.data[0].id).trim());
    }
  }, [jobPositions.data, selectedPositionId]);

  // Effect that validates and fixes selectedPositionId
  useEffect(() => {
    if (selectedPositionId && jobPositions.data && jobPositions.data.length > 0) {
      // Validate that the selected ID exists among the current positions
      const positionExists = jobPositions.data.some(
        pos => String(pos.id).trim() === String(selectedPositionId).trim()
      );

      if (!positionExists) {
        console.warn(
          "Selected position ID not found among the available positions. Selecting the first available position.",
          selectedPositionId
        );
        setSelectedPositionId(String(jobPositions.data[0].id).trim());
      }
    }
  }, [selectedPositionId, jobPositions.data]);

  useEffect(() => {
    if (jobPositions.data) {
      try {
        jobPositions.data.forEach(position => {
          if (position.id && typeof position.id === 'string' && position.id.trim() !== position.id) {
            position.id = position.id.trim();
          }
        });
      } catch (e) {
        console.warn('Could not clean up the position IDs:', e);
      }
    }
  }, [jobPositions.data]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isThankYouVisible) {
      timer = setTimeout(() => {
        handleCloseThankYou();
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isThankYouVisible]);

  const handleCloseThankYou = () => {
    setIsThankYouVisible(false);
    form.resetFields();
    setResumeFile(null);
  };

  // Resume upload handler
  const handleResumeUpload = (file: UploadFile) => {
    if (validateFileFormat(file)) {
      setResumeFile(file as unknown as File);
    }
    return false;
  };

  // onFinish handler
  const onFinish = (values: any) => {
    if (!resumeFile) {
      message.error('Please attach your resume');
      return;
    }

    // Debug log: show every available position with its ID
    console.log("Available positions:", jobPositions.data?.map(p => ({
      id: p.id,
      title: p.title
    })));

    let positionId = selectedPositionId;
    console.log("Initially selected position ID:", positionId);

    // If no position is selected but there is only one, use it
    if (!positionId && jobPositions.data && jobPositions.data.length === 1) {
      positionId = jobPositions.data[0].id;
      console.log("Using the ID of the only available position:", positionId);
    }
    // If no position is selected but positions exist, use the first one
    else if (!positionId && jobPositions.data && jobPositions.data.length > 0) {
      positionId = jobPositions.data[0].id;
      console.log("Using the ID of the first available position:", positionId);
    }

    if (!positionId) {
      message.error('No position selected');
      return;
    }

    // Make sure the ID is a valid string with no extra whitespace
    const cleanPositionId = String(positionId).trim();
    console.log("Cleaned position ID:", cleanPositionId);

    // Check whether the ID exists among the available positions
    const positionExists = jobPositions.data?.some(pos => String(pos.id).trim() === cleanPositionId);

    if (!positionExists) {
      message.error('The selected position is not available');
      console.error("Position ID not found among the available positions:", cleanPositionId);
      return;
    }

    // Check whether the ID looks like a valid UUID
    const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(cleanPositionId);

    if (!isValidUUID) {
      message.error('Invalid position ID. Please select another position.');
      console.error("Position ID is not a valid UUID:", cleanPositionId);
      return;
    }

    // Combine firstName e lastName em fullName
    const formData = {
      fullName: `${values.firstName} ${values.lastName}`,
      email: values.email,
      phone: values.phone,
      positionId: cleanPositionId,
      message: values.message || ''
    };

    console.log("Submitting application with data:", formData);

    // Find the title of the selected position
    const positionTitle = jobPositions.data?.find(pos => String(pos.id).trim() === cleanPositionId)?.title || '';
    setAppliedPositionTitle(positionTitle);

    createJobApplication.mutate(
      {
        data: formData,
        resume: resumeFile
      },
      {
        onSuccess: () => {
          setIsThankYouVisible(true);
        },
        onError: (error: any) => {
          console.error('Error submitting application:', error);
          const errorMessage = error?.response?.data?.message || 'We could not submit your application. Please try again.';
          message.error(errorMessage);
        }
      }
    );
  };

  const showJobForm = (position?: Position) => {
    setIsEditing(!!position);

    if (position) {
      // Make sure the ID is a valid string
      const positionId = String(position.id).trim();
      setEditingPositionId(positionId);
      console.log("Preparing to edit position:", positionId, position.title);

      // Check whether the arrays are JSON strings or already arrays
      let responsibilities = position.responsibilities;
      if (typeof responsibilities === 'string') {
        try {
          responsibilities = JSON.parse(responsibilities);
        } catch (e) {
          console.warn("Error parsing responsibilities:", e);
          responsibilities = position.description ?
            position.description.split('\n').filter(item => item.trim() !== '') :
            ['', ''];
        }
      }

      let requirements = position.requirements;
      if (typeof requirements === 'string') {
        try {
          requirements = JSON.parse(requirements);
        } catch (e) {
          console.warn("Error parsing requirements:", e);
          requirements = ['', '', ''];
        }
      }

      let benefits = position.benefits;
      if (typeof benefits === 'string') {
        try {
          benefits = JSON.parse(benefits);
        } catch (e) {
          console.warn("Error parsing benefits:", e);
          benefits = ['', ''];
        }
      }

      // Populate the form with the position data for editing
      jobPositionForm.setFieldsValue({
        ...position,
        title: position.title.trim(), // Trim extra whitespace
        responsibilities: responsibilities || ['', ''],
        requirements: requirements || ['', '', ''],
        benefits: benefits || ['', '']
      });
    } else {
      // Clear the form for a new position
      setEditingPositionId(null);
      jobPositionForm.resetFields();
      // Pre-fill with the default values for a Snow Removal Operator position
      jobPositionForm.setFieldsValue({
        title: 'SNOW REMOVAL OPERATOR',
        type: 'Seasonal',
        location: 'Tahoe, CA',
        department: 'Operations',
        seasonalPeriod: `${getCurrentSeason()} winter season`,
        responsibilities: [
          'Operate snow plow equipment to clear snow and ice.',
          'Safely navigate and maneuver the snow plow vehicle.'
        ],
        requirements: [
          'Previous experience with snow plow equipment is preferred.',
          'Valid driver\'s license with a clean driving record.',
          'Strong attention to detail and ability to follow instructions.'
        ],
        additionalInfo: 'This is a salaried position that only requires you to work when it snows. Please submit your resume for consideration.',
        salaryRange: '$2,500.00 - $2,950.00 per month',
        contractPeriod: 'November - April'
      });
    }

    setIsJobFormVisible(true);
  };

  const handleJobFormCancel = () => {
    setIsJobFormVisible(false);
    setIsEditing(false);
    setEditingPositionId(null);
    jobPositionForm.resetFields();
  };

  const handleJobFormSubmit = (values: JobPositionFormValues) => {
    // Filter empty values from the lists
    const filteredResponsibilities = values.responsibilities?.filter(item => item.trim() !== '') || [];
    const filteredRequirements = values.requirements.filter(item => item.trim() !== '');
    const filteredBenefits = values.benefits?.filter(item => item.trim() !== '') || [];

    // Clean the title by trimming leading and trailing whitespace
    const cleanedTitle = values.title.trim();

    const formattedValues = {
      ...values,
      title: cleanedTitle,
      responsibilities: filteredResponsibilities.length > 0 ? filteredResponsibilities : undefined,
      requirements: filteredRequirements,
      benefits: filteredBenefits.length > 0 ? filteredBenefits : undefined
    };

    if (isEditing && editingPositionId) {
      // Make sure the editing ID is a valid string
      const cleanId = String(editingPositionId).trim();

      if (!cleanId) {
        message.error("Invalid position ID for editing");
        return;
      }

      console.log("Editing position with ID:", cleanId);

      // Update the existing position - including the ID in the request body
      updateJobPosition.mutate({
        id: cleanId,
        data: {
          id: cleanId, // Important: also include the ID in the data object
          ...formattedValues,
          featured: false,
          datePosted: new Date().toISOString()
        }
      }, {
        onSuccess: () => {
          message.success("Position updated successfully!");
          setIsJobFormVisible(false);
          setIsEditing(false);
          setEditingPositionId(null);
          jobPositionForm.resetFields();
        },
        onError: (error: any) => {
          console.error("Error updating position:", error);
          const errorMessage = error?.response?.data?.message || 'Failed to update the position';
          message.error(errorMessage);
        }
      });
    }
    else {
      addJobPosition.mutate({
        ...formattedValues,
        featured: false,
        datePosted: new Date().toISOString()
      }, {
        onSuccess: (response) => {
          const createdPosition = response;
          console.log("Position created successfully:", createdPosition);

          if (createdPosition && createdPosition.id) {
            // Update the selected ID with the real UUID returned by the API
            console.log("Updating the selected position to the real ID:", createdPosition.id);
            setSelectedPositionId(String(createdPosition.id).trim());
          }

          message.success("New position created successfully!");
          setIsJobFormVisible(false);
          jobPositionForm.resetFields();
        },
        onError: (error: any) => {
          console.error("Error creating position:", error);
          const errorMessage = error?.response?.data?.message || 'Failed to create the new position';
          message.error(errorMessage);
        }
      });
    }
  };

  const handleDeletePosition = (id: string) => {
    if (!id) {
      message.error("Invalid ID for removing the position");
      return;
    }

    const cleanId = String(id).trim();

    if (!cleanId) {
      message.error("Invalid ID for removing the position");
      return;
    }

    console.log("Removing position with ID:", cleanId);

    removeJobPosition.mutate(cleanId, {
      onSuccess: () => {
        message.success("Position removed successfully!");
        if (selectedPositionId && String(selectedPositionId).trim() === cleanId && jobPositions.data && jobPositions.data.length > 0) {
          // If the current position is removed, select the first available one
          const remainingPositions = jobPositions.data.filter(pos => String(pos.id).trim() !== cleanId);
          if (remainingPositions.length > 0) {
            setSelectedPositionId(String(remainingPositions[0].id).trim());
          } else {
            setSelectedPositionId(null);
          }
        }
      },
      onError: (error: any) => {
        console.error("Error removing position:", error);
        const errorMessage = error?.response?.data?.message || 'Failed to remove the position';
        message.error(errorMessage);
      }
    });
  };

  // Scrolls to the form
  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderSelectedPosition = () => {
    if (jobPositions.isLoading) {
      return (
        <div className="bg-white container-single-cut max-w-3xl w-full flex justify-center items-center min-h-[150px] p-6 mx-auto mb-16">
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4 text-charcoal">Loading positions...</p>
          </div>
        </div>
      );
    }

    // Check whether there are no positions
    if (!jobPositions.data || jobPositions.data.length === 0) {
      return (
        <div className="bg-white container-single-cut max-w-3xl w-full p-6 mx-auto mb-16 text-center">
          <span className="font-azonix !font-normal text-dark text-xl md:text-2xl uppercase mt-10">
            No positions available at the moment.
          </span>
        </div>
      );
    }

    // Make sure the ID used for comparison is a clean string
    const cleanSelectedId = selectedPositionId ? String(selectedPositionId).trim() : null;

    // Log para debug
    if (cleanSelectedId) {
      console.log("Looking for position with ID:", cleanSelectedId);
    }

    // Locate the selected position
    const position = jobPositions.data.find(pos => {
      const posId = String(pos.id).trim();
      return posId === cleanSelectedId;
    });

    if (!position) {
      console.warn("No position found with the ID:", cleanSelectedId);
      // Select the first available position if none was found
      if (jobPositions.data.length > 0 && cleanSelectedId) {
        const firstPosition = jobPositions.data[0];
        setSelectedPositionId(String(firstPosition.id).trim());
        console.log("Selecting the first available position:", firstPosition.id);
        return null;
      }
      return null;
    }

    // Log para debug
    console.log("Position found:", position.id, position.title);

    // Parse requirements, benefits and responsibilities that come as JSON strings
    let requirements = position.requirements;
    if (typeof requirements === 'string') {
      try {
        requirements = JSON.parse(requirements);
      } catch (e) {
        requirements = [];
        console.warn("Error parsing requirements:", e);
      }
    }

    let benefits = position.benefits;
    if (typeof benefits === 'string') {
      try {
        benefits = JSON.parse(benefits);
      } catch (e) {
        benefits = [];
        console.warn("Error parsing benefits:", e);
      }
    }

    // Split the description into individual responsibility items
    let respItems = [];
    if (position.description) {
      // Convert the description into an array of responsibilities when it comes as a newline-separated string
      respItems = position.description.split('\n').filter(item => item.trim() !== '');
    } else if (position.responsibilities) {
      if (typeof position.responsibilities === 'string') {
        try {
          respItems = JSON.parse(position.responsibilities);
        } catch (e) {
          respItems = [];
          console.warn("Error parsing responsibilities:", e);
        }
      } else {
        respItems = position.responsibilities;
      }
    }

    return (
      <div className="bg-white max-w-3xl w-full p-6 mx-auto mb-16">
        {/* Admin controls */}
        {isAuthenticated && (
          <div className="flex justify-end mb-5 -mt-2 -mr-2">
            <Space>
              <Button
                icon={<PencilSimple size={16} />}
                onClick={() => showJobForm(position)}
                className="btn-single-cut !h-8"
              >
                EDIT
              </Button>
              <Popconfirm
                title="Remove position"
                description="Are you sure you want to remove this position?"
                onConfirm={() => handleDeletePosition(position.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ className: "btn-single-cut-cancel" }}
                cancelButtonProps={{ className: "btn-single-cut" }}
                overlayClassName="remove-position-popconfirm"
              >
                <Button
                  icon={<DeleteOutlined />}
                  className="btn-single-cut-cancel !h-8 !px-3 !font-normal"
                >
                  REMOVE
                </Button>
              </Popconfirm>
            </Space>
          </div>
        )}

        <Title level={2} className="font-azonix !font-normal text-charcoal text-center mb-4">
          {position.title}
        </Title>

        {position.seasonalPeriod && (
          <Paragraph className="text-center text-black !text-lg mb-6 font-coda">
            {position.seasonalPeriod}
          </Paragraph>
        )}

        <div className="border-t-4 text-primary my-4 -mx-6"></div>

        <div className="text-left text-black space-y-4 px-2 md:px-5 py-2 font-coda">
          {/* Responsibilities are always shown as separate list items */}
          {respItems.length > 0 && (
            <ul className="list-none space-y-2">
              {respItems.map((responsibility: string, index: number) => (
                <li key={index} className="text-black font-coda">{responsibility}</li>
              ))}
            </ul>
          )}

          {/* Requisitos */}
          {requirements && requirements.length > 0 && (
            <>
              <p className="font-azonix !font-normal !text-black tracking-wider">Requirements:</p>
              <ul className="list-disc list-inside space-y-2">
                {requirements.map((requirement: string, index: number) => (
                  <li key={index} className="text-black font-coda">{requirement}</li>
                ))}
              </ul>
            </>
          )}

          {/* Benefits */}
          {benefits && benefits.length > 0 && (
            <>
              <p className="font-azonix !font-normal !text-black tracking-wider">Benefits:</p>
              <ul className="list-disc list-inside space-y-2">
                {benefits.map((benefit: string, index: number) => (
                  <li key={index} className="text-black font-coda">{benefit}</li>
                ))}
              </ul>
            </>
          )}

          {/* Pay */}
          {position.salaryRange && (
            <p className="text-black font-coda"><span className="font-azonix !font-normal !text-black tracking-wider">Pay:</span> {position.salaryRange}</p>
          )}

          {/* Contract period */}
          {position.contractPeriod && (
            <p className="text-black font-coda"><span className="font-azonix !font-normal !text-black tracking-wider">Contract Period:</span> {position.contractPeriod}</p>
          )}
        </div>
        <div className="text-center mt-8">
          <Button
            onClick={scrollToForm}
            block
            className="btn-single-cut-dark-text !text-base !w-fit">
            APPLY NOW
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div
      className="w-full"
      style={{
        backgroundImage: "url('/assets/images/background-darker.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
      {/* Hero Section */}
      <section className="bg-dark w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto px-4 py-12">
          <Title level={1} className="font-azonix !font-normal text-primary text-center pt-10">
            JOIN OUR TEAM
          </Title>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="text-light text-center md:text-lg">
              We're looking for experienced operators to join the best team in the Tahoe region.
            </Paragraph>
          </div>

          {/* Admin controls to add new position */}
          {isAuthenticated && (
            <div className="mt-6">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showJobForm()}
                className="btn-double-cut !w-fit"
              >
                ADD NEW POSITION
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Main Content Section */}
      <section>
        <div className="container mx-auto flex flex-col items-center justify-center p-6 md:p-12">
          {/* Position selector (only visible when there is more than one position) */}
          {jobPositions.data && jobPositions.data.length > 1 && (
            <div className="w-full max-w-3xl mb-6">
              <Select
                className="w-full position-selector"
                value={selectedPositionId}
                onChange={(value) => setSelectedPositionId(String(value).trim())}
                placeholder="Select a position"
                options={jobPositions.data.map(pos => ({
                  value: String(pos.id).trim(),
                  label: `${pos.title} - ${pos.location}`
                }))}
              />
            </div>
          )}

          {/* Selected position card */}
          {renderSelectedPosition()}

          {/* Form Container */}
          <div className="bg-white p-8 w-full max-w-xl" ref={formRef}>
            <Title level={3} className="font-azonix !font-normal text-center max-w-md mx-auto !mb-6">
              JOIN OUR TEAM OF OPERATORS
            </Title>
            <Form
              name="join_form"
              onFinish={onFinish}
              layout="vertical"
              autoComplete="off"
              form={form}
              requiredMark={false}
              disabled={createJobApplication.isPending}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="firstName" label={<span className="text-charcoal">First Name</span>} rules={[{ required: true, message: "Please enter your first name" }]}>
                    <div><Input placeholder="First Name" /></div>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="lastName" label={<span className="text-charcoal">Last Name</span>} rules={[{ required: true, message: "Please enter your last name" }]}>
                    <div><Input placeholder="Last Name" /></div>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="email" label={<span className="text-charcoal">Email Address</span>} rules={[{ required: true, message: "Please enter your email" }, { type: 'email', message: "Please enter a valid email" }]}>
                <div><Input placeholder="Email Address" /></div>
              </Form.Item>
              <Form.Item name="phone" label={<span className="text-charcoal">Phone Number</span>} rules={[{ required: true, message: "Please enter your phone number" }]}>
                <div><InputMask mask="usphone" placeholder="000 000-0000" /></div>
              </Form.Item>
              <Form.Item name="message" label={<span className="text-charcoal">Cover Letter/Message (Optional)</span>}>
                <div><TextArea rows={4} placeholder="Tell us why you're interested in this position" /></div>
              </Form.Item>
              <Form.Item className="w-fit" name="resume" label={<span className="text-charcoal">Attach Resume</span>} rules={[{ required: true, message: "Please upload your resume" }]}>
                <Upload
                  name="resume"
                  listType="text"
                  accept={FILE_ACCEPT_STRING}
                  beforeUpload={handleResumeUpload}
                  maxCount={1}
                  showUploadList={{
                    showRemoveIcon: true,
                    removeIcon: <DeleteOutlined />,
                  }}
                >
                  <Button type="default" icon={<UploadOutlined />} className="!font-normal !h-8 !p-1">Upload Resume</Button>
                </Upload>
              </Form.Item>
              <div className="text-right my-6">
                <Button
                  htmlType="submit"
                  block
                  className="btn-double-cut !w-fit"
                  loading={createJobApplication.isPending}
                >
                  {createJobApplication.isPending ? "SUBMITTING..." : "APPLY"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </section>

      {/* Thank You Modal */}
      <Modal
        title={null}
        open={isThankYouVisible}
        footer={null}
        closable={false}
        centered
        className="application-thank-you-modal"
        width={600}
        maskClosable={true}
        onCancel={handleCloseThankYou}
      >
        <div className="text-center p-4">
          <CheckCircleOutlined className="text-primary text-6xl mb-6" />
          <Title level={3} className="font-azonix !font-normal text-charcoal mb-6">THANK YOU FOR YOUR APPLICATION</Title>
          <Paragraph className="text-charcoal text-lg mb-6">
            We've received your application for the <span className="text-primary">{appliedPositionTitle}</span> position. Our team will carefully review your resume and qualifications.
          </Paragraph>
          <Paragraph className="text-gray-500 text-sm">
            This message will close automatically in a few seconds...
          </Paragraph>
        </div>
      </Modal>

      {/* Modal to add/edit job position */}
      <Modal
        title={isEditing ? "Edit Position" : "Add New Position"}
        open={isJobFormVisible}
        onCancel={handleJobFormCancel}
        footer={null}
        centered
        width={800}
        destroyOnClose={true}
      >
        <Form
          form={jobPositionForm}
          layout="vertical"
          onFinish={handleJobFormSubmit}
          initialValues={{
            requirements: [""],
            responsibilities: [""],
            benefits: [""]
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Position Title"
                rules={[{ required: true, message: "Please enter the position title" }]}
              >
                <Input placeholder="Ex: SNOW REMOVAL OPERATOR" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="location"
                label="Location"
                rules={[{ required: true, message: "Please enter the location" }]}
              >
                <Input placeholder="Ex: Tahoe, CA" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Contract Type"
                rules={[{ required: true, message: "Please select the contract type" }]}
              >
                <Select placeholder="Select type" options={[
                  { value: "Full-time", label: "Full-time" },
                  { value: "Part-time", label: "Part-time" },
                  { value: "Contract", label: "Contract" },
                  { value: "Seasonal", label: "Seasonal" }
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
                rules={[{ required: true, message: "Please select the department" }]}
              >
                <Select placeholder="Select department" options={[
                  { value: "Operations", label: "Operations" },
                  { value: "Management", label: "Management" },
                  { value: "Snow Removal", label: "Snow Removal" },
                  { value: "Maintenance", label: "Maintenance" },
                  { value: "Administration", label: "Administration" }
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="salaryRange"
                label="Salary Range"
                rules={[{ required: true, message: "Please enter the salary range" }]}
              >
                <Input placeholder="Ex: $2,500.00 - $2,950.00 per month" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contractPeriod"
                label="Contract Period"
              >
                <Input placeholder="Ex: November - April" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="seasonalPeriod"
                label="Seasonal Period"
              >
                <Input placeholder={`Ex: ${getCurrentSeason()} winter season`} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Position Description"
          >
            <TextArea rows={3} placeholder="General description of the position (optional)" />
          </Form.Item>

          <Divider className="text-white tracking-wider">Responsibilities</Divider>

          <Form.List name="responsibilities">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ width: '740px' }}
                    >
                      <Input placeholder="Responsibility" />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <DeleteOutlined onClick={() => remove(name)} />
                    ) : null}
                  </Space>
                ))}
                <Form.Item className="text-center">
                  <Button className="btn-single-cut !w-fit" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Responsibility
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Divider className="text-white tracking-wider">Requirements</Divider>

          <Form.List name="requirements">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={name}
                      rules={[{ required: true, message: 'Please enter a requirement' }]}
                      style={{ width: '740px' }}
                    >
                      <Input placeholder="Requirement" />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <DeleteOutlined onClick={() => remove(name)} />
                    ) : null}
                  </Space>
                ))}
                <Form.Item className="text-center">
                  <Button className="btn-single-cut !w-fit" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Requirement
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Divider className="text-white tracking-wider">Benefits</Divider>

          <Form.List name="benefits">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ width: '740px' }}
                    >
                      <Input placeholder="Benefit" />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <DeleteOutlined onClick={() => remove(name)} />
                    ) : null}
                  </Space>
                ))}
                <Form.Item className="text-center">
                  <Button className="btn-single-cut !w-fit" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Benefit
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div className="flex justify-end mt-4">
            <Space>
              <Button className="!font-normal tracking-wider" onClick={handleJobFormCancel}>CANCEL</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={addJobPosition.isPending || updateJobPosition.isPending}
                className="btn-single-cut"
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
